import axios from "axios";
import { userStore, router, types, apiService as apiServiceAuth, globalStore } from "@/BaseComponent";

const BASE_URL = process.env.VUE_APP_API_URL;

export const header = {
    baseURL: BASE_URL,
    headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        "X-AUTH-TOKEN": "",
    },
};

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

const setInterceptors = (axiosService: any) => {
    const user = userStore();
    const global = globalStore();

    // Request Interceptor
    axiosService.interceptors.request.use(
        async (config: any) => {
            if (user.accessToken) {
                config.headers["X-AUTH-TOKEN"] = "Bearer " + user.accessToken;
            }

            return config;
        },
        (error: any) => Promise.reject(error),
    );

    // Response Interceptor
    axiosService.interceptors.response.use(
        (response: any) => response,
        async (error: any) => {
            const originalRequest = error.config;

            let returnCode = 0;

            if (error.request.responseType === "blob") {
                const text = await error.response.data.text();
                const jsonObj = JSON.parse(text);
                returnCode = jsonObj.returnCode;
            }

            // Token 만료 에러만 잡기
            if ((returnCode === 11 || error.response?.data?.returnCode === 11) && !originalRequest._retry) {
                originalRequest._retry = true;

                // 이미 refresh 진행 중 → 기존 promise 기다렸다가 진행
                if (global.isRefreshing) {
                    return refreshPromise!.then((newToken) => {
                        originalRequest.headers["X-AUTH-TOKEN"] = "Bearer " + newToken;
                        return axiosService(originalRequest);
                    });
                }

                // refresh 최초 실행
                global.setRefreshing(true);
                refreshPromise = (async () => {
                    try {
                        const refreshParam: types.ReqRefreshToken = {
                            refreshToken: user.refreshToken!,
                        };

                        const response: types.ResCommon = await apiServiceAuth.base.refresh(refreshParam);

                        if (response.returnCode === 0) {
                            const { accessToken, refreshToken: newRefreshToken } = response;

                            // store 갱신
                            user.setAccessToken(accessToken);
                            user.setRefreshToken(newRefreshToken);
                            user.setExpiresIn(response.expiresIn);

                            user.startTimer();

                            return accessToken;
                        } else {
                            user.$reset();
                            global.$reset();
                            router.push("/login");
                            return "";
                        }
                    } catch (refreshError) {
                        user.$reset();
                        global.$reset();
                        router.push("/login");
                        throw refreshError;
                    } finally {
                        global.setRefreshing(false);
                        refreshPromise = null;
                    }
                })();

                // 새 토큰 발급 후 원래 요청 재시도
                const newToken = await refreshPromise;
                originalRequest.headers["X-AUTH-TOKEN"] = "Bearer " + newToken;
                return axiosService(originalRequest);
            } else if (error.request.responseType === "blob") {
                const text = await error.response.data.text();
                const jsonObj = JSON.parse(text);
                error.response.data = jsonObj;
            }

            return Promise.reject(error);
        },
    );

    return axiosService;
};

export const apiService = () => {
    const axiosService = axios.create(header);
    return setInterceptors(axiosService);
};
